import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  ChangeEvent,
} from "react";
import ReactHtmlParser from "react-html-parser";
import IBankForm, {
  TInvestorBankAccountDetails,
  TBankDetails,
} from "./IBankForm";
import { Input } from "@appComponents/Input/Input";
import { displayBankNumber, catchError } from "@features/utils/functions";
import { dropdownSelectRef } from "@appComponents/Dropdown/Dropdown";
import { FundsDispatchContext, FundsAppContext } from "@features/context";
import FundsService from "@services/Funds.service";
import countries, { mappedCountries } from "@features/staticData/countries";
import { MyLink } from "@features/utils/reactFunctions";
import { RestrictedContainer } from "@appComponents/RestrictedContainer/RestrictedContainer";
import { connect } from "react-redux";
import { TReducers } from "@features/reducers";
import Button from "@appComponents/Button/Button";
import FormInput from "../FormInput";
import { setBankFormDataAvailable } from "@funds/features/action.funds";
import NoticeIcon from "@funds/pages/MyFunds/pages/StakingPage/parts/StakeForm/danger.svg";
import Notice from "@funds/pages/MyFunds/pages/StakingPage/parts/shared/Notice";

import * as styles from "./BankFormStyle.module.scss";
import * as inputStyles from "@appComponents/Input/InputStyle.module.scss";

export type TSelect = any;

const BankForm: React.FunctionComponent<IBankForm> = (props: IBankForm) => {
  const [accountNumberFormat, setAccountNumberFormat] = useState(false);
  const [investorAccountDetails, setInvestorAccountDetails] =
    useState<TInvestorBankAccountDetails | null>(null);
  const [bankDetails, setBankDetails] = useState<TBankDetails | null>(null);
  const dispatch = useContext(FundsDispatchContext);
  const { investorId } = useContext(FundsAppContext);
  const bankStatementInputRef = useRef<HTMLInputElement>(null);
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (dropdownSelectRef && dropdownSelectRef.current) {
      dropdownSelectRef.current.disabled = true;
    }
    return () => {
      if (dropdownSelectRef && dropdownSelectRef.current) {
        dropdownSelectRef.current.disabled = false;
      }
    };
  }, []);

  useEffect(() => {
    if (props.values.bic_swift) {
      setIsEdit(true);
    }
  }, []);

  useEffect(() => {
    investorId &&
      FundsService.getInvestorIdentityData(investorId).then((res) => {
        setInvestorAccountDetails(res.data);
        props.setBankFormDataAvailable(res.data.address_valid);
      });

    return () => {
      props.setBankFormDataAvailable(true);
    };
  }, []);

  useEffect(() => {
    if (props.values.bic_swift && !props.errors.bic_swift) {
      FundsService.getBankDetails(props.values.bic_swift)
        .then((res) => {
          if (props.fiatRestrictedCountries.includes(res.data.country_code)) {
            dispatch({
              type: "setErrors",
              errors: {
                bic_swift_async: `Our partner bank is currently restricting operations from bank accounts in ${
                  countries.find(
                    (country) => country.slug === res.data.country_code
                  )?.name
                }.`,
              },
            });
          } else {
            dispatch({ type: "cleanSwiftExternalError" });
          }
          setBankDetails(res.data);
        })
        .catch((err) => {
          if (err.response.status === 404) {
            dispatch({
              type: "setErrors",
              errors: {
                bic_swift_async:
                  "Provided SWIFT/BIC code seems to be valid" +
                  " however we could not find relevant bank data." +
                  " Please use another swift code.",
              },
            });
          } else {
            catchError(err);
          }
        });
    } else if (props.errors.bic_swift && bankDetails !== null) {
      setBankDetails(null);
    }
  }, [props.errors.bic_swift, props.values.bic_swift]);

  const displayBaseErrors = () =>
    props.errors.base && <p className={styles.error}>{props.errors.base}</p>;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;
    dispatch({ type: "edit", payload: { value, name } });
  };

  const onBankStatementChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target?.files;
    if (files && files[0]) {
      dispatch({
        type: "edit",
        payload: { name: "bankStatement", value: files[0] },
      });
    }
  };

  return props.bankFormAddressDataAvailable &&
    ["verified", "fully_verified"].includes(props.verificationStatus) ? (
    <>
      <div className={styles.bankForm}>
        <FormInput
          formItemProps={{
            label: "BIC/SWIFT",
            name: "bic_swift",
            help:
              props.values.bic_swift !== undefined
                ? ReactHtmlParser(
                    (
                      props.errors.bic_swift || props.errors.bic_swift_async
                    )?.replace(".", ".<br />") ?? ""
                  )
                : "",
          }}
          inputProps={{
            onChange: handleInputChange,
            defaultValue: props.values.bic_swift || "",
            name: "bic_swift",
          }}
        />
        <FormInput
          formItemProps={{
            label: "Account Number",
            name: "account_number",
            help:
              props.values.account_number !== undefined
                ? props.errors.account_number
                : "",
          }}
          inputProps={{
            onChange: handleInputChange,
            defaultValue: accountNumberFormat
              ? displayBankNumber(props.values.account_number, " - ")
              : props.values.account_number || "",
            name: "account_number",
            onFocus: () => setAccountNumberFormat(false),
            onBlur: () => setAccountNumberFormat(true),
          }}
        />
      </div>
      <FormInput
        formItemProps={{
          label: "Beneficiary Name",
        }}
        inputProps={{
          value: investorAccountDetails?.name || "",
          disabled: true,
        }}
      />
      <FormInput
        formItemProps={{
          label: "Beneficiary Address Line 1",
        }}
        inputProps={{
          value: investorAccountDetails?.address.line_1 || "",
          disabled: true,
        }}
      />
      {investorAccountDetails?.address.line_2 && (
        <FormInput
          formItemProps={{
            label: "Beneficiary Address Line 2",
          }}
          inputProps={{
            value: investorAccountDetails?.address.line_2 || "",
            disabled: true,
          }}
        />
      )}
      <FormInput
        formItemProps={{
          label: "Beneficiary Address Postcode",
        }}
        inputProps={{
          value: investorAccountDetails?.address.postcode || "",
          disabled: true,
        }}
      />

      <FormInput
        formItemProps={{
          label: "Beneficiary City",
        }}
        inputProps={{
          value: investorAccountDetails?.address.city || "",
          disabled: true,
        }}
      />

      <FormInput
        formItemProps={{
          label: "Beneficiary Country",
        }}
        inputProps={{
          value: investorAccountDetails?.address.country_name || "",
          disabled: true,
        }}
      />

      <FormInput
        formItemProps={{
          label: "Bank Name",
        }}
        inputProps={{
          value: bankDetails?.name || "",
          disabled: true,
        }}
      />

      {bankDetails?.branch && (
        <FormInput
          formItemProps={{
            label: "Bank Branch",
          }}
          inputProps={{
            value: bankDetails.branch || "",
            disabled: true,
          }}
        />
      )}

      <FormInput
        formItemProps={{
          label: "Bank City",
        }}
        inputProps={{
          value: bankDetails?.city || "",
          disabled: true,
        }}
      />

      <FormInput
        formItemProps={{
          label: "Bank Country",
        }}
        inputProps={{
          value:
            mappedCountries.find((c) => c.value === bankDetails?.country_code)
              ?.label || "",
          disabled: true,
        }}
      />

      {isEdit && (
        <div className={styles.presentation}>
          <label className={inputStyles.inputLabel}>
            In order to review your account, we need to verify that you are the
            owner of the new account. Please upload a bank statement matching
            your name and address and issued within the last 3 months.
          </label>
          <input
            type="file"
            ref={bankStatementInputRef}
            className={styles.fileInput}
            accept="application/pdf,image/*"
            onChange={onBankStatementChange}
          />
          <Button
            className={styles.uploadBtn}
            onClick={() => {
              bankStatementInputRef.current?.click();
            }}
          >
            Upload Bank Statement
          </Button>
          <div>
            <Input disabled value={props.values.bankStatement?.name || ""} />
          </div>
        </div>
      )}

      <Notice
        message="Note!"
        icon={<NoticeIcon />}
        description={
          <>
            The name and address on your BF account must match the name and
            address on your bank account. If it does not match you will need to
            re-verify your BF account or update the details with your bank
            account to your current legal name and residential address. To
            update your BF profile with us you will need to{" "}
            <MyLink to={"/redirect_to_identity_verification"}>re-verify</MyLink>{" "}
            through our BF ID system here.
          </>
        }
        type="warning"
        showIcon
        style={{ margin: "40px auto" }}
      />
      {displayBaseErrors()}
    </>
  ) : (
    <RestrictedContainer center={true}>
      <div className={styles.message}>
        {props.isCompany ? (
          <>
            Some essential data is missing for your organization. Please contact{" "}
            <MyLink to={"/support"}>support</MyLink> to proceed with adding your
            organization bank account.
          </>
        ) : (
          <>
            In order to add a bank account you must have verified account. Click{" "}
            <MyLink to={"/redirect_to_identity_verification"}>here</MyLink> to
            continue.
          </>
        )}
      </div>
    </RestrictedContainer>
  );
};

const mapStateToProps = (store: TReducers) => ({
  bankFormAddressDataAvailable: store.fundsReducer.bankFormAddressDataAvailable,
  isCompany: !!store.companyReducer.companyDetails.investor_id,
  fiatRestrictedCountries: store.appSettings.fiat_restricted_countries,
  verificationStatus: store.userInfo.verification_status,
});

const mapDispatchToProps = (dispatch: Function) => {
  return {
    setBankFormDataAvailable: (data: boolean) => {
      dispatch(setBankFormDataAvailable(data));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BankForm);
